/**
 * Navigation Main Section JavaScript
 * Handles navigation menu interactions, sticky header, and mobile menu functionality
 */

(function() {
    "use strict";

    // Webpack module system
    var webpackModules = {
        // Module 2483: Main Navigation Component
        2483: function(module, exports, require) {
            var navUtils = require(4442);
            var overlayManager = require(4519);

            /**
             * Main Navigation Custom Element
             * Handles navigation menu behavior (click/hover), sticky header, and cart drawer
             */
            class MainNav extends HTMLElement {
                connectedCallback() {
                    // Initialize elements
                    this.headerEl = document.getElementById("nav-wrapper");
                    this.navigationItems = this.querySelectorAll("toggletip-engine");
                    this.cartOpenButtons = this.querySelectorAll("button[name='cart-open']");
                    this.cartDrawer = document.querySelector("drawer-component");
                    this.ux = this.dataset.ux; // 'click' or 'hover'
                    this.uxPosition = this.dataset.uxPosition; // sticky position setting

                    // Initialize header properties
                    this.headerEl.props = {};
                    this.headerEl.props.navMargin = 0;
                    this.headerEl.props.transformAmount = 0;
                    this.headerEl.props.lastScroll = window.scrollY;
                    this.headerEl.props.submenuOpen = false;
                    this.headerEl.props.activeMenu = 0;

                    // Setup
                    this.resetHeader();
                    
                    // Observe sticky subheader if needed
                    if (this.uxPosition === "off" || this.uxPosition === "hide") {
                        this.observeStickySubheader();
                    }

                    // Add scroll listener if sticky is enabled
                    if (this.uxPosition !== "off") {
                        window.addEventListener("scroll", this.handleScroll);
                    }

                    this.resetOnResize();
                    this.handleCartDrawer();
                    this.enableNavUX();
                }

                /**
                 * Reset header to initial state
                 */
                resetHeader = () => {
                    this.headerEl.classList.remove("header-visible");
                    this.headerEl.classList.remove("sticky-active");
                    this.headerEl.classList.remove("reached-top");
                    this.headerEl.style.marginTop = "";
                    document.getElementById("header-spacer").style.paddingTop = "";
                };

                /**
                 * Handle scroll events for sticky header
                 */
                handleScroll = () => {
                    navUtils.handleStickyScroll(this.headerEl, this.uxPosition);
                };

                /**
                 * Enable navigation UX (click or hover)
                 */
                enableNavUX() {
                    // Set sticky class based on position
                    if (this.uxPosition === "fixed") {
                        this.headerEl.classList.add("sticky");
                    } else {
                        this.headerEl.classList.remove("sticky");
                    }

                    // Handle click or hover UX
                    if (this.ux === "click") {
                        // Click mode: toggle menu on click
                        // Note: Individual triggers handle their own clicks, this is a fallback
                        this.navigationItems.forEach((item) => {
                            item.addEventListener("click", (event) => {
                                // Don't handle if click was on trigger (trigger handles it)
                                if (event.target.closest("[data-tooltip-trigger]")) {
                                    return;
                                }
                                event.stopPropagation();
                                if (item.isOpen) {
                                    overlayManager.closeAllToggletips(false);
                                } else {
                                    overlayManager.closeAllToggletips(true, item);
                                    item.open();
                                }
                            });
                        });

                        // Close menus when clicking outside
                        this.closest(".shopify-section").addEventListener("click", (event) => {
                            // Don't close if clicking on trigger or inside toggletip-engine
                            if (!event.target.closest("toggletip-engine") && 
                                !event.target.closest("[data-tooltip-trigger]")) {
                                overlayManager.closeAllToggletips();
                            }
                        });
                    } else {
                        // Hover mode: open on mouseenter, close on mouseleave
                        this.navigationItems.forEach((item) => {
                            if (item.querySelector(".search-trigger")) {
                                // Search trigger uses click
                                item.addEventListener("click", (event) => {
                                    event.stopPropagation();
                                    if (item.isOpen) {
                                        overlayManager.closeAllToggletips(false);
                                    } else {
                                        overlayManager.closeAllToggletips(true, item);
                                        item.open();
                                    }
                                });
                            } else {
                                // Regular menu items use hover
                                item.addEventListener("mouseenter", () => {
                                    item.open();
                                });
                                item.addEventListener("mouseleave", () => {
                                    item.close();
                                });

                                // Handle dropdown hover
                                const tooltip = item.querySelector("[data-tooltip]");
                                if (tooltip && item.handleDropdownEnter && item.handleDropdownLeave) {
                                    tooltip.addEventListener("mouseenter", () => {
                                        item.handleDropdownEnter();
                                    });
                                    tooltip.addEventListener("mouseleave", () => {
                                        item.handleDropdownLeave();
                                    });
                                }
                            }
                        });
                    }

                    // Initialize navigation menus
                    this.querySelectorAll(".nav").forEach((nav) => {
                        navUtils.closeSecondaryMenu(nav);
                    });

                    // Setup navigation menu interactions
                    this.querySelectorAll(".nav").forEach((nav) => {
                        nav.props = {};
                        const mainMenuLink = nav.querySelector(".nav__main-menu-link");
                        const secondaryMenuItem = mainMenuLink?.parentNode.querySelector(".nav__secondary-menu__item");
                        const multilevelContent = this.querySelector(".nav--multilevel .nav__content");

                        // Set height for multilevel menu
                        if (secondaryMenuItem && multilevelContent) {
                            const height = secondaryMenuItem.getBoundingClientRect().height;
                            multilevelContent.style.height = height + "px";
                        }

                        // Add event listeners to main menu links
                        nav.querySelectorAll(".nav__main-menu-link").forEach((link, index) => {
                            // Desktop hover
                            if (window.matchMedia("(hover: hover)").matches && 
                                window.matchMedia("(min-width: 1024px)").matches) {
                                link.addEventListener("mouseover", () => {
                                    navUtils.activateMenuLink(link, nav);
                                });
                            }

                            // Focus (keyboard navigation)
                            link.addEventListener("focus", () => {
                                navUtils.activateMenuLink(link, nav);
                            });

                            // Click
                            link.addEventListener("click", () => {
                                nav.classList.add("nav--secondary-menu-open");
                                navUtils.activateMenuLink(link, nav);
                                
                                const targetMenuId = link.getAttribute("data-targetmenu");
                                nav.querySelector(`#${targetMenuId}`);
                                
                                // Reset scroll on mobile
                                if (nav.classList.contains("nav--mobile")) {
                                    nav.querySelector(".container").scrollTop = 0;
                                }
                            });
                        });

                        // Close buttons
                        nav.querySelectorAll(".nav__secondary-menu__close").forEach((closeBtn) => {
                            closeBtn.addEventListener("click", () => {
                                navUtils.closeSecondaryMenu(nav);
                            });
                        });

                        // Handle mobile column opener buttons (open second column)
                        nav.querySelectorAll(".nav__mobile-column-opener").forEach((opener) => {
                            opener.addEventListener("click", (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                
                                // Open secondary menu column
                                nav.classList.add("nav--secondary-menu-open");
                                navUtils.activateMenuLink(opener, nav);
                                
                                // Reset scroll on mobile
                                if (nav.classList.contains("nav--mobile")) {
                                    nav.querySelector(".container").scrollTop = 0;
                                }
                            });
                        });

                        // Initialize mobile dropdowns
                        navUtils.initMobileDropdowns(nav, true);
                    });
                }

                /**
                 * Observe sticky subheader intersection
                 */
                observeStickySubheader() {
                    const stickySubheader = document.querySelector(".sticky-subheader");
                    if (!stickySubheader) return;

                    stickySubheader.props = { isStuck: false };

                    new IntersectionObserver(
                        (entries) => {
                            const [entry] = entries;
                            const isStuck = entry.intersectionRatio < 1;
                            entry.target.classList.toggle("sticky--stuck", isStuck);
                            
                            if (entry.target.props.isStuck !== isStuck) {
                                entry.target.props.isStuck = isStuck;
                            }
                        },
                        { threshold: [1] }
                    ).observe(stickySubheader);
                }

                /**
                 * Reset header on resize
                 */
                resetOnResize() {
                    const mobileMediaQuery = window.matchMedia("(max-width: 1024px)");
                    mobileMediaQuery.addEventListener("change", () => {
                        overlayManager.closeAllToggletips();
                        if (mobileMediaQuery.matches) {
                            this.headerEl.style.marginTop = 0;
                            this.headerEl.props.navMargin = 0;
                        }
                    });
                }

                /**
                 * Handle cart drawer opening
                 */
                handleCartDrawer() {
                    if (!this.cartOpenButtons) return;

                    this.cartOpenButtons.forEach((button) => {
                        button.addEventListener("click", () => {
                            overlayManager.closeAllToggletips(true);
                            this.cartDrawer.open();
                        });
                    });
                }

                disconnectedCallback() {
                    if (this.uxPosition !== "off") {
                        window.removeEventListener("scroll", this.handleScroll);
                    }
                }
            }

            // Register custom element
            if (!window.customElements.get("main-nav")) {
                try {
                    window.customElements.define("main-nav", MainNav);
                } catch (e) {
                    console.error('Error defining main-nav custom element:', e);
                }
            }
        },

        // Module 4442: Navigation Utilities
        4442: function(module, exports, require) {
            /**
             * Close secondary menu
             */
            function closeSecondaryMenu(navElement) {
                let secondaryMenu;
                
                navElement.classList.remove("nav--secondary-menu-open");
                secondaryMenu = navElement.classList.contains("nav--mobile")
                    ? navElement.querySelector(".nav--mobile__inner-container")
                    : navElement.querySelector(".nav__secondary-menu");

                if (window.matchMedia("(max-width: 1023px)").matches) {
                    const activeLink = navElement.querySelector(".nav__main-menu-link--active");
                    if (activeLink) {
                        activeLink.classList.remove("nav__main-menu-link--active");
                    }
                    
                    // Remove duplicate active links
                    const duplicateActive = navElement.querySelector(".nav__main-menu-link--active");
                    if (duplicateActive) {
                        duplicateActive.classList.remove("nav__main-menu-link--active");
                    }

                    // Reset animation classes
                    navElement.querySelectorAll(".nav__secondary-menu__item--animate-in").forEach((item) => {
                        item.classList.remove("nav__secondary-menu__item--animate-in");
                        item.classList.remove("nav__secondary-menu__item--visible");
                        secondaryMenu.height = "auto";
                    });
                }
            }

            /**
             * Toggle mobile dropdown wrapper
             */
            function toggleMobileDropdown(isOpen, wrapper) {
                if (isOpen) {
                    wrapper.classList.add("nav__mobile-dropdown-wrapper--open");
                } else {
                    wrapper.classList.remove("nav__mobile-dropdown-wrapper--open");
                }
            }

            /**
             * Initialize mobile dropdowns
             */
            function initMobileDropdowns(navElement, enabled) {
                navElement.querySelectorAll(".nav__mobile-dropdown-wrapper").forEach((wrapper) => {
                    const opener = wrapper.querySelector(".nav__mobile-accordion-opener");
                    if (!opener) return;

                    // Skip if this is a column opener (handled separately)
                    if (opener.classList.contains("nav__mobile-column-opener")) return;

                    // Skip if already initialized
                    if (opener.dataset.dropdownInitialized === "true") return;
                    opener.dataset.dropdownInitialized = "true";

                    opener.addEventListener("click", (event) => {
                        // Stop all event propagation immediately
                        event.stopImmediatePropagation();
                        event.stopPropagation();
                        event.preventDefault();

                        // Get current state BEFORE any changes
                        const isCurrentlyOpen = wrapper.classList.contains("nav__mobile-dropdown-wrapper--open");

                        // Close other dropdowns in the same column
                        const column = opener.closest(".nav--mobile__column");
                        if (column) {
                            column.querySelectorAll(".nav__mobile-dropdown-wrapper")
                                .forEach((otherWrapper) => {
                                    if (otherWrapper !== wrapper) {
                                        otherWrapper.classList.remove("nav__mobile-dropdown-wrapper--open");
                                    }
                                });
                        }

                        // Toggle current dropdown: add if closed, remove if open
                        if (isCurrentlyOpen) {
                            wrapper.classList.remove("nav__mobile-dropdown-wrapper--open");
                        } else {
                            wrapper.classList.add("nav__mobile-dropdown-wrapper--open");
                        }
                    }, { once: false, capture: false });
                });
            }

            /**
             * Activate menu link and show corresponding submenu
             */
            function activateMenuLink(link, navElement) {
                const targetMenuId = link.getAttribute("data-targetmenu");
                const targetMenu = navElement.querySelector(`#${targetMenuId}`);

                // Don't activate if already active
                if (link.classList.contains("nav__main-menu-link--active")) {
                    return;
                }

                // Set active menu index
                navElement.querySelectorAll(".nav__main-menu-link:not(.simple)").forEach((linkItem, index) => {
                    navElement.props.activeMenu = index;
                });

                // Remove active state from all links
                navElement.querySelectorAll(".nav__main-menu-link").forEach((linkItem) => {
                    linkItem.classList.remove("nav__main-menu-link--active");
                    linkItem.setAttribute("aria-expanded", "false");
                });

                // Activate current link
                link.classList.add("nav__main-menu-link--active");
                link.setAttribute("aria-expanded", "true");

                // Adjust mega menu height
                const secondaryContent = link.parentNode.querySelector(".nav__secondary-navigation-item-content");
                if (secondaryContent) {
                    const megaMenuContent = document.querySelector(".nav--mega_menu .nav__content");
                    if (window.innerHeight - 140 > secondaryContent.offsetHeight) {
                        megaMenuContent.style.height = secondaryContent.offsetHeight + 80 + "px";
                    } else {
                        megaMenuContent.style.height = window.innerHeight - 140 + "px";
                    }
                }

                // Reset animation classes
                navElement.querySelectorAll(".nav__secondary-menu__item").forEach((item) => {
                    item.classList.remove("nav__secondary-menu__item--animate-in");
                });

                // Show target menu
                if (targetMenu) {
                    targetMenu.classList.add("nav__secondary-menu__item--visible");
                }

                // Animate menu items
                setTimeout(() => {
                    navElement.querySelectorAll(".nav__secondary-menu__item").forEach((item) => {
                        if (item !== targetMenu) {
                            item.classList.remove("nav__secondary-menu__item--animate-in", "nav__secondary-menu__item--visible");
                            item.setAttribute("aria-hidden", "true");
                        } else {
                            item.classList.add("nav__secondary-menu__item--visible");
                            item.setAttribute("aria-hidden", "false");
                            requestAnimationFrame(() => {
                                item.classList.add("nav__secondary-menu__item--animate-in");
                            });
                        }
                    });
                }, 300);
            }

            // Sticky scroll handler
            let stickyPriorityHeight = 0;
            const stickySubheader = document.querySelector(".sticky-subheader");

            /**
             * Handle sticky header scroll behavior
             */
            function handleStickyScroll(headerElement, position) {
                const isFixed = position === "fixed";
                const isHide = position === "hide";
                const scrollDelta = window.scrollY - headerElement.props.lastScroll;
                const headerSpacer = document.getElementById("header-spacer");
                const stickyPriority = document.querySelector(".sticky-priority");

                // Update nav margin
                headerElement.props.navMargin += scrollDelta + stickyPriorityHeight;
                
                if (stickyPriority && stickyPriorityHeight === 0) {
                    stickyPriorityHeight = stickyPriority.offsetHeight;
                }
                if (!stickyPriority) {
                    stickyPriorityHeight = 0;
                }

                // Handle hide behavior
                if (isHide && headerElement.props.navMargin >= headerElement.offsetHeight) {
                    headerElement.classList.remove("header-visible");
                    headerElement.querySelectorAll("toggletip-engine").forEach((item) => {
                        if (item.isOpen) item.close();
                    });
                } else {
                    headerElement.classList.add("header-visible");
                }

                // Reset margin if submenu is open
                if (headerElement.props.submenuOpen) {
                    headerElement.props.navMargin = 0;
                } else {
                    headerElement.props.navMargin = Math.max(
                        0,
                        Math.min(headerElement.props.navMargin, headerElement.offsetHeight)
                    );
                }

                // Apply margin for hide mode
                if (isHide && !headerElement.submenuOpen) {
                    headerElement.style.marginTop = -headerElement.props.navMargin + "px";
                }

                // Handle sticky subheader with fixed position
                if (stickySubheader && isFixed) {
                    headerElement.props.transformAmount = -headerElement.props.navMargin + headerElement.offsetHeight;
                    stickySubheader.style.top = headerElement.offsetHeight + "px";
                    
                    const isStuck = stickySubheader.getBoundingClientRect().top === headerElement.getBoundingClientRect().bottom;
                    stickySubheader.classList.toggle("sticky--stuck", isStuck);
                    headerElement.style.boxShadow = isStuck ? "none" : "";
                }

                // Handle sticky subheader with hide position
                if (stickySubheader && isHide) {
                    if (stickySubheader.props.isStuck) {
                        headerElement.props.transformAmount = -headerElement.props.navMargin + headerElement.offsetHeight;
                        headerElement.classList.add("sticky-active");
                        stickySubheader.style.marginTop = -headerElement.props.transformAmount + "px";
                        stickySubheader.style.paddingTop = headerElement.props.transformAmount + "px";
                    } else {
                        headerElement.classList.remove("sticky-active");
                        stickySubheader.style.marginTop = 0;
                        stickySubheader.style.paddingTop = 0;
                    }
                }

                // Update scroll position
                const currentScroll = Math.max(
                    0,
                    Math.min(window.scrollY, document.body.offsetHeight - window.innerHeight)
                );
                headerElement.props.lastScroll = currentScroll;

                // Update reached-top class
                if (currentScroll === 0) {
                    headerElement.classList.add("reached-top");
                    headerSpacer.style.paddingTop = 0;
                } else {
                    headerElement.classList.remove("reached-top");
                    headerSpacer.style.paddingTop = headerElement.offsetHeight + "px";
                }
            }

            // Export functions after they are defined
            exports.handleStickyScroll = handleStickyScroll;
            exports.closeSecondaryMenu = closeSecondaryMenu;
            exports.activateMenuLink = activateMenuLink;
            exports.initMobileDropdowns = initMobileDropdowns;
        },

        // Module 1434: Overlay Manager
        1434: function(module, exports) {
            const OVERLAY_CLASS = "above-overlay";
            const overlayInstances = new Set();

            /**
             * Overlay Manager Class
             * Handles overlay display for modals, dropdowns, etc.
             */
            class OverlayManager {
                isClosingTimer = null;
                attachedElements = new Set();

                constructor(container = document.querySelector("#main-content")) {
                    this.container = container;
                    this.overlay = document.createElement("div");
                    this.overlay.innerHTML = "&nbsp;";
                    this.overlay.classList.add("overlay");

                    this.overlay.addEventListener("transitionend", (event) => {
                        if (event.propertyName !== "opacity" || this.isOpen) return;

                        // Cleanup when closed
                        if (this.attachedElements && this.attachedElements.size) {
                            this.attachedElements.forEach((element) => {
                                this.detachElement(element);
                            });
                        }

                        if (this.afterClosed) {
                            this.afterClosed();
                            this.afterClosed = null;
                        }

                        this.detachOverlay();
                    });

                    this.overlay.addEventListener("transitionend", (event) => {
                        if (event.propertyName === "opacity" && this.isOpen && this.afterOpened) {
                            this.afterOpened();
                            this.afterOpened = null;
                        }
                    });

                    overlayInstances.add(this);
                }

                attachOverlay() {
                    this.container.appendChild(this.overlay);
                }

                detachOverlay() {
                    this.container.removeChild(this.overlay);
                }

                async open(options = {}) {
                    const { attachElements, afterOpened, beforeOpened } = options;

                    await this.attachOverlay();

                    if (this.isClosingTimer) {
                        clearTimeout(this.isClosingTimer);
                        this.isClosingTimer = null;
                    }

                    if (beforeOpened) beforeOpened();

                    if (attachElements) {
                        attachElements.forEach((element) => {
                            this.attachedElements.add(element);
                        });

                        this.attachedElements.forEach((element) => {
                            element.classList.add(OVERLAY_CLASS);
                        });
                    }

                    requestAnimationFrame(() => {
                        this.overlay.classList.add("open");
                    });

                    if (afterOpened) {
                        this.afterOpened = afterOpened;
                    }
                }

                close(options = {}) {
                    const { delay, beforeClosed, afterClosed } = options;

                    if (beforeClosed) beforeClosed();
                    this.afterClosed = afterClosed;

                    const removeOverlay = () => {
                        requestAnimationFrame(() => {
                            this.overlay.classList.remove("open");
                        });
                    };

                    if (delay) {
                        this.isClosingTimer = setTimeout(removeOverlay, delay);
                    } else {
                        removeOverlay();
                    }
                }

                detachElement(element) {
                    if (this.attachedElements.has(element) && element.classList.contains(OVERLAY_CLASS)) {
                        element.classList.remove(OVERLAY_CLASS);
                        this.attachedElements.delete(element);
                    }
                }

                get isOpen() {
                    return this.overlay.classList.contains("open");
                }

                onClick(handler) {
                    if (handler) {
                        this.overlay.addEventListener("click", handler);
                    }
                }

                removeOnClick(handler) {
                    if (handler) {
                        this.overlay.removeEventListener("click", handler);
                    }
                }

                onHover(handler) {
                    if (handler) {
                        this.overlay.addEventListener("mouseover", (event) => {
                            if (this.isOpen) handler(event);
                        });
                    }
                }
            }

            exports.OverlayManager = OverlayManager;
        },

        // Module 4519: Toggletip Engine
        4519: function(module, exports, require) {
            var OverlayManager = require(1434);
            var utils = require(6072);
            
            // Try to load positioning utils, use fallback if not available
            var positioningUtils;
            
            // Check if module exists before requiring
            var moduleExists = false;
            try {
                // Access webpackModules through require function's context
                // Since we're inside a module function, we can check if module 3011 exists
                var posUtilsModule = require(3011);
                
                // Check if module loaded successfully (has required methods)
                if (posUtilsModule && typeof posUtilsModule === 'object' && 
                    typeof posUtilsModule.offset === 'function' && 
                    typeof posUtilsModule.computePosition === 'function') {
                    positioningUtils = posUtilsModule;
                    moduleExists = true;
                } else {
                    // Module exists but doesn't have required methods - use fallback
                    console.warn('Positioning utils module 3011 loaded but missing required methods, using fallback');
                }
            } catch (e) {
                // Module doesn't exist or failed to load
                moduleExists = false;
            }
            
            if (!moduleExists) {
                // Using fallback positioning utils
                // Fallback: create minimal positioning utils compatible with floating-ui API
                // Middleware functions return objects that are passed to computePosition
                positioningUtils = {
                    offset: function(offsetValue) {
                        // Return middleware object
                        // offsetValue can be a number or an object with mainAxis/crossAxis
                        var offset = 0;
                        if (typeof offsetValue === 'number') {
                            offset = offsetValue;
                        } else if (offsetValue && typeof offsetValue === 'object') {
                            offset = offsetValue.mainAxis || 0;
                        }
                        
                        return {
                            name: 'offset',
                            fn: function(middlewareArguments) {
                                return {
                                    x: middlewareArguments.x,
                                    y: middlewareArguments.y + offset
                                };
                            }
                        };
                    },
                    flip: function(config) {
                        return {
                            name: 'flip',
                            fn: function(middlewareArguments) {
                                return middlewareArguments;
                            }
                        };
                    },
                    shift: function(config) {
                        return {
                            name: 'shift',
                            fn: function(middlewareArguments) {
                                return middlewareArguments;
                            }
                        };
                    },
                    arrow: function(config) {
                        return {
                            name: 'arrow',
                            fn: function(middlewareArguments) {
                                var arrowElement = config && config.element;
                                if (arrowElement) {
                                    return {
                                        x: middlewareArguments.x,
                                        y: middlewareArguments.y,
                                        data: {
                                            arrow: {
                                                x: 0,
                                                y: 0
                                            }
                                        }
                                    };
                                }
                                return middlewareArguments;
                            }
                        };
                    },
                    size: function(config) {
                        return {
                            name: 'size',
                            fn: function(middlewareArguments) {
                                // Call apply function if provided
                                if (config && typeof config.apply === 'function') {
                                    try {
                                        config.apply({
                                            availableWidth: window.innerWidth,
                                            availableHeight: window.innerHeight,
                                            rects: {
                                                reference: { width: 0, height: 0 },
                                                floating: { width: 0, height: 0 }
                                            }
                                        });
                                    } catch (e) {
                                        // Ignore errors in fallback
                                    }
                                }
                                return middlewareArguments;
                            }
                        };
                    },
                    computePosition: function(trigger, popover, options) {
                        // Simple positioning: place popover below trigger
                        var triggerRect = trigger.getBoundingClientRect();
                        var offset = 20; // Default offset
                        
                        // Try to get popover dimensions
                        var popoverRect;
                        try {
                            popoverRect = popover.getBoundingClientRect();
                        } catch (e) {
                            popoverRect = { width: 0, height: 0 };
                        }
                        
                        // Calculate position
                        var x = triggerRect.left;
                        var y = triggerRect.bottom + offset;
                        
                        // Keep within viewport
                        var padding = 16;
                        if (popoverRect.width > 0) {
                            if (x + popoverRect.width > window.innerWidth - padding) {
                                x = window.innerWidth - popoverRect.width - padding;
                            }
                            if (x < padding) {
                                x = padding;
                            }
                            
                            // Ensure popover is visible vertically
                            if (y + popoverRect.height > window.innerHeight - padding) {
                                y = triggerRect.top - popoverRect.height - offset;
                                if (y < padding) {
                                    y = padding;
                                }
                            }
                        }
                        
                        return Promise.resolve({ 
                            x: x,
                            y: y,
                            placement: 'bottom-start', 
                            middlewareData: { 
                                arrow: { x: 0, y: 0 },
                                offset: { x: 0, y: offset }
                            } 
                        });
                    }
                };
            }

            const overlay = new OverlayManager.OverlayManager();

            /**
             * Toggletip Engine Custom Element
             * Handles tooltip/popover display and positioning
             */
            class ToggletipEngine extends HTMLElement {
                constructor() {
                    super();

                    this.headerEl = document.getElementById("nav-wrapper");
                    this.headerUXPosition = document.querySelector(".header")?.getAttribute("data-ux-position");
                    this.predictiveSearch = this.querySelector("predictive-search");
                    this.mobileView = this.dataset.mobileView || "floating";
                    this.isWithinHeader = this.closest("#nav-wrapper");
                    this.overlayAttachingElements = this.isWithinHeader
                        ? [this.headerEl]
                        : [this.closest(".shopify-section"), this.headerEl];
                    this.navigationItemTrigger = this.querySelector("[data-tooltip-trigger]");
                    this.navigationItemTriggers = this.querySelectorAll("[data-tooltip-trigger]");
                    this.navigationItemPopover = this.querySelector("[data-tooltip]");
                    this.arrowElement = this.querySelector("#arrow");
                    this.spacing = 16;
                    this.offset = 20;
                    
                    // Store UX mode for this toggletip
                    const header = document.querySelector(".header");
                    const mainNav = header?.querySelector("main-nav");
                    this.uxMode = mainNav?.dataset?.ux || "hover";
                    
                    // Store overlay click handler for cleanup
                    this.overlayClickHandler = null;

                    this.navigationItemPopover.addEventListener("click", (event) => {
                        event.stopPropagation();
                    });

                    // Toggle function that can be called by any trigger
                    const toggleToggletip = (event) => {
                        // Check if it's a link - prevent default navigation if needed
                        const trigger = event.currentTarget;
                        if (trigger.tagName === "A" && trigger.hasAttribute("href")) {
                            // Check UX mode from header
                            const header = document.querySelector(".header");
                            const mainNav = header?.querySelector("main-nav");
                            const uxMode = mainNav?.dataset?.ux;
                            
                            // Only prevent default if we're in click mode
                            if (uxMode === "click") {
                                event.preventDefault();
                            }
                        }
                        
                        // Stop propagation immediately to prevent other handlers from firing
                        event.stopImmediatePropagation();
                        event.stopPropagation();
                        
                        // Toggle the toggletip
                        if (this.isOpen) {
                            this.close();
                        } else {
                            // Close other toggletips first (but not this one)
                            const section = this.closest(".shopify-section");
                            if (section) {
                                section.querySelectorAll("toggletip-engine").forEach((engine) => {
                                    if (engine !== this && engine.isOpen) {
                                        engine.close();
                                    }
                                });
                            }
                            this.open();
                        }
                    };

                    // Add click handler for all trigger elements
                    if (this.navigationItemTriggers && this.navigationItemTriggers.length > 0) {
                        this.navigationItemTriggers.forEach((trigger) => {
                            trigger.addEventListener("click", toggleToggletip);
                        });
                    }

                    this.closeOnScroll = this.closeOnScroll.bind(this);

                    this.addEventListener("click", (event) => {
                        event.stopPropagation();
                    });

                    const section = this.closest(".shopify-section");
                    if (section) {
                        section.addEventListener("click", (event) => {
                            // Don't close if clicking on trigger or inside toggletip-engine
                            if (!event.target.closest("toggletip-engine") && 
                                !event.target.closest("[data-tooltip-trigger]")) {
                                closeAllToggletipsInSection(section);
                            }
                        });
                    }

                    this.handleResize = utils.debounce(() => {
                        if (this.isOpen) {
                            if (window.matchMedia("(max-width: 1023px)").matches && this.mobileView === "fullscreen") {
                                this.updateMobileFullscreen();
                            } else {
                                this.update();
                            }
                        }
                    }, 100);

                    this.container = this.closest(".shopify-section")?.querySelector(".section__wrapper");
                }

                /**
                 * Update popover position
                 */
                update() {
                    const trigger = this.navigationItemTrigger;
                    const popover = this.navigationItemPopover;
                    const containerRect = this.container?.getBoundingClientRect();

                    const middleware = [
                        positioningUtils.offset(this.offset),
                        positioningUtils.flip({
                            boundary: "viewport",
                            padding: {
                                left: containerRect?.left || 0,
                                right: window.innerWidth - (containerRect?.right || window.innerWidth),
                            },
                        }),
                        positioningUtils.shift({
                            boundary: "viewport",
                            padding: {
                                left: containerRect?.left || 0,
                                right: window.innerWidth - (containerRect?.right || window.innerWidth),
                            },
                            alignment: "start",
                        }),
                        positioningUtils.arrow({ element: this.arrowElement }),
                        positioningUtils.size({
                            apply(options) {
                                const { availableWidth, availableHeight } = options;
                                const maxWidth = (containerRect?.width || 0) > 1600
                                    ? 1600
                                    : (containerRect?.width || 0) || Math.min(window.innerWidth, 1600);

                                Object.assign(popover.style, {
                                    maxWidth: `${maxWidth}px`,
                                    maxHeight: popover.classList.contains("tooltip--mobile") ? "" : "640px",
                                    height: popover.classList.contains("tooltip--mobile") ? `${availableHeight}px` : "",
                                });
                            },
                            padding: this.spacing,
                            boundary: "viewport",
                        }),
                    ];

                    positioningUtils.computePosition(trigger, popover, {
                        placement: "bottom-start",
                        middleware: middleware,
                        boundary: "viewport",
                    }).then((result) => {
                        const { x, y, placement, middlewareData } = result;
                        // Commented out: setting top and left for data-tooltip
                        // Object.assign(popover.style, { left: `${x}px`, top: `${y}px` });

                        if (!this.arrowElement) return;

                        const { x: arrowX, y: arrowY } = middlewareData.arrow;
                        const sideMap = {
                            top: "bottom",
                            right: "left",
                            bottom: "top",
                            left: "right",
                        };
                        const side = sideMap[placement.split("-")[0]];

                        Object.assign(this.arrowElement.style, {
                            left: arrowX != null ? `${arrowX}px` : "",
                            top: arrowY != null ? `${arrowY}px` : "",
                            right: "",
                            bottom: "",
                            [side]: `-${this.spacing}px`,
                        });
                    });
                }

                /**
                 * Update mobile fullscreen position
                 */
                updateMobileFullscreen() {
                    // Commented out: setting top, left, bottom for data-tooltip
                    // Object.assign(this.navigationItemPopover.style, {
                    //     left: `${this.spacing}px`,
                    //     top: `${this.spacing}px`,
                    //     bottom: `${this.spacing}px`,
                    // });
                }

                /**
                 * Scroll popover into view
                 */
                scrollIntoView(element) {
                    if (this.headerUXPosition !== "off") {
                        const rect = element.getBoundingClientRect();
                        window.scrollTo(rect.left, rect.top + window.scrollY - this.headerEl.clientHeight);
                    } else {
                        element.scrollIntoView();
                    }
                }

                /**
                 * Open toggletip
                 */
                open() {
                    if (this.isOpen) return;

                    const isMobile = window.matchMedia("(max-width: 1023px)").matches;

                    if (isMobile) {
                        if (this.mobileView === "fullscreen") {
                            if (this.headerUXPosition !== "off") {
                                this.headerEl.style.marginTop = `-${this.headerEl.clientHeight}px`;
                                this.headerEl.props.lastScroll = window.scrollY;
                                this.headerEl.props.navMargin = this.headerEl.clientHeight;
                            }
                            this.navigationItemPopover.removeAttribute("style");
                            this.updateMobileFullscreen();
                            overlay.open({ attachElements: [this.closest(".shopify-section")] });
                        } else {
                            this.navigationItemPopover.removeAttribute("style");
                            this.update();
                            overlay.open({ attachElements: [this.closest(".shopify-section"), this.headerEl] });
                        }
                        utils.disableBodyScroll(true);
                    } else {
                        this.navigationItemPopover.removeAttribute("style");
                        this.update();
                        overlay.open({ attachElements: this.overlayAttachingElements });

                        // Commented out: scroll to navigation popover
                        // if (!this.isWithinHeader) {
                        //     setTimeout(() => {
                        //         this.scrollIntoView(this.navigationItemPopover);
                        //     }, 100);
                        // }

                        // Only add scroll listener for hover mode, not click mode
                        // In click mode, menu should stay open even when scrolling
                        if (this.uxMode !== "click") {
                            document.addEventListener("scroll", this.closeOnScroll);
                        }
                    }

                    this.navigationItemTrigger.setAttribute("open", "true");
                    
                    // Add delay to prevent immediate close when opening
                    // Add overlay onClick to close menu when clicking on overlay (works for both click and hover modes)
                    setTimeout(() => {
                        this.overlayClickHandler = () => {
                            this.close();
                        };
                        overlay.onClick(this.overlayClickHandler);
                    }, 100);

                    if (this.predictiveSearch) {
                        this.predictiveSearch.open();
                    }

                    utils.enableKeyboardClose(true, this.close.bind(this));
                    window.addEventListener("resize", this.handleResize);
                }

                /**
                 * Close toggletip
                 */
                close(force = false) {
                    if (!this.isOpen) return;

                    this.navigationItemTrigger.removeAttribute("open");
                    // Commented out: setting top and left for data-tooltip when closing
                    // Object.assign(this.navigationItemPopover.style, {
                    //     top: "-9999px",
                    //     left: "-9999px",
                    //     visibility: "hidden",
                    //     opacity: "0",
                    //     zIndex: "-1",
                    // });

                    if (force) {
                        this.overlayAttachingElements.forEach((element) => {
                            overlay.detachElement(element);
                        });
                    }

                    // Remove overlay click handler if it was added
                    if (this.overlayClickHandler) {
                        overlay.removeOnClick(this.overlayClickHandler);
                        this.overlayClickHandler = null;
                    }
                    
                    overlay.close();
                    if (this.predictiveSearch) {
                        this.predictiveSearch.close();
                    }

                    utils.enableKeyboardClose(false, this.close.bind(this));

                    if (window.matchMedia("(max-width: 1023px)").matches) {
                        utils.disableBodyScroll(false);
                        this.headerEl.style.marginTop = "0px";
                    } else {
                        // Remove scroll listener if it was added (only in hover mode)
                        document.removeEventListener("scroll", this.closeOnScroll);
                    }

                    window.removeEventListener("resize", this.handleResize);
                }

                /**
                 * Close on scroll
                 */
                closeOnScroll() {
                    // Don't close on scroll in click mode - menu should stay open
                    if (this.uxMode === "click") {
                        return; // Don't close on scroll in click mode
                    }
                    
                    if (!utils.isElementInViewport(this) && this.isOpen) {
                        this.close();
                    }
                }

                get isOpen() {
                    return this.navigationItemTrigger.getAttribute("open") === "true" && overlay.isOpen;
                }

                disconnectedCallback() {
                    window.removeEventListener("resize", this.handleResize);
                    if (this.isOpen) {
                        this.close(true);
                    }
                }
            }

            // Register custom element
            if (!window.customElements.get("toggletip-engine")) {
                try {
                    window.customElements.define("toggletip-engine", ToggletipEngine);
                } catch (e) {
                    console.error('Error defining toggletip-engine custom element:', e);
                }
            }

            /**
             * Close all toggletips in a section
             */
            function closeAllToggletipsInSection(section) {
                section.querySelectorAll("toggletip-engine").forEach((engine) => {
                    if (engine.isOpen) engine.close();
                });
            }

            /**
             * Close all toggletips
             */
            function closeAllToggletips(closeOverlay = false, except = null) {
                const allToggletips = document.querySelectorAll("toggletip-engine");
                
                if (except) {
                    allToggletips.forEach((engine) => {
                        if (engine !== except && engine.isOpen) {
                            engine.close(closeOverlay);
                        }
                    });
                } else {
                    allToggletips.forEach((engine) => {
                        if (engine.isOpen) {
                            engine.close(closeOverlay);
                        }
                    });
                }
            }

            // Export function after it's defined
            exports.closeAllToggletips = closeAllToggletips;
        },

        // Module 6072: Utility Functions
        6072: function(module, exports) {

            /**
             * Disable/enable body scroll
             */
            function disableBodyScroll(disable) {
                const scrollbarWidth = window.innerWidth - document.body.offsetWidth;
                const navWrapper = document.getElementById("nav-wrapper");

                if (disable) {
                    document.body.classList.add("no-scroll");
                    document.body.style.paddingRight = scrollbarWidth + "px";
                    navWrapper.style.paddingRight = scrollbarWidth + "px";
                } else {
                    document.body.classList.remove("no-scroll");
                    document.body.style.paddingRight = "0px";
                    navWrapper.style.paddingRight = "0px";
                }
            }

            let escapeKeyHandler = () => {};

            /**
             * Enable keyboard close (Escape key)
             */
            function enableKeyboardClose(enable, callback) {
                if (callback) {
                    escapeKeyHandler = callback;
                }

                const keydownHandler = (event) => {
                    if (event.key === "Escape") {
                        escapeKeyHandler();
                        window.removeEventListener("keydown", keydownHandler);
                    }
                };

                if (enable) {
                    window.addEventListener("keydown", keydownHandler);
                } else {
                    window.removeEventListener("keydown", keydownHandler);
                }
            }

            /**
             * Debounce function
             */
            function debounce(func, wait) {
                const context = this;
                let timeout;

                return function() {
                    const args = Array.from(arguments);
                    clearTimeout(timeout);
                    timeout = setTimeout(function() {
                        return func.apply(context, args);
                    }, wait);
                };
            }

            /**
             * Handle local scroll links
             */
            document.querySelectorAll(".local-scroll").forEach((link) => {
                link.addEventListener("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const targetSelector = event.target.getAttribute("href");
                    
                    function scrollToElement(element, offset = 0) {
                        const navWrapper = document.getElementById("nav-wrapper");
                        const headerPosition = document.querySelector(".header")?.getAttribute("data-ux-position");
                        const elementTop = element.getBoundingClientRect().top;

                        window.scrollTo({
                            top: headerPosition !== "off"
                                ? elementTop + window.scrollY - offset + parseFloat(navWrapper.style.marginTop)
                                : elementTop + window.scrollY - offset,
                            behavior: "smooth",
                        });
                    }

                    scrollToElement(document.querySelector(targetSelector));
                });
            });

            /**
             * Check if element is in viewport
             */
            function isElementInViewport(element, threshold = 120) {
                const rect = element.getBoundingClientRect();
                const html = document.documentElement;

                return (
                    rect.top >= -threshold &&
                    rect.left >= -threshold &&
                    rect.bottom <= (window.innerHeight || html.clientHeight) + threshold &&
                    rect.right <= (window.innerWidth || html.clientWidth) + threshold
                );
            }

            // Export functions after they are defined
            exports.debounce = debounce;
            exports.enableKeyboardClose = enableKeyboardClose;
            exports.disableBodyScroll = disableBodyScroll;
            exports.isElementInViewport = isElementInViewport;
        },
    };

    // Webpack module loader
    var moduleCache = {};

    function requireModule(moduleId) {
        var cached = moduleCache[moduleId];
        if (cached !== undefined) return cached.exports;

        // Check if module exists
        if (!webpackModules[moduleId]) {
            console.warn('Module ' + moduleId + ' not found');
            var emptyModule = { exports: {} };
            moduleCache[moduleId] = emptyModule;
            return emptyModule.exports;
        }

        var module = (moduleCache[moduleId] = { exports: {} });
        try {
            webpackModules[moduleId](module, module.exports, requireModule);
        } catch (e) {
            console.error('Error loading module ' + moduleId + ':', e);
            console.error(e.stack);
            var errorModule = { exports: {} };
            moduleCache[moduleId] = errorModule;
            return errorModule.exports;
        }
        return module.exports;
    }

    // Initialize webpack
    requireModule.m = webpackModules;
    var chunkQueue = [];

    requireModule.O = function(chunkId, promises, onFulfilled, priority) {
        if (!promises) {
            var minPriority = Infinity;
            for (var i = 0; i < chunkQueue.length; i++) {
                promises = chunkQueue[i][0];
                onFulfilled = chunkQueue[i][1];
                priority = chunkQueue[i][2];

                var allResolved = true;
                if (promises && Array.isArray(promises)) {
                    for (var j = 0; j < promises.length; j++) {
                        // Check if chunk is loaded using O.j if available, otherwise check O[chunkId]
                        var isLoaded = false;
                        if (requireModule.O.j && typeof requireModule.O.j === 'function') {
                            isLoaded = requireModule.O.j(promises[j]);
                        } else if (requireModule.O[promises[j]]) {
                            isLoaded = true;
                        }
                        if (!isLoaded) {
                            allResolved = false;
                            break;
                        }
                    }
                } else {
                    allResolved = false;
                }

                if (allResolved) {
                    chunkQueue.splice(i--, 1);
                    var result = onFulfilled();
                    if (result !== undefined) chunkId = result;
                } else {
                    if (priority < minPriority) minPriority = priority;
                }
            }
            return chunkId;
        }

        priority = priority || 0;
        for (var i = chunkQueue.length; i > 0 && chunkQueue[i - 1][2] > priority; i--) {
            chunkQueue[i] = chunkQueue[i - 1];
        }
        chunkQueue[i] = [promises, onFulfilled, priority];
    };

    requireModule.d = function(exports, definition) {
        for (var key in definition) {
            if (requireModule.o(definition, key) && !requireModule.o(exports, key)) {
                Object.defineProperty(exports, key, { enumerable: true, get: definition[key] });
            }
        }
    };

    requireModule.o = function(object, property) {
        return Object.prototype.hasOwnProperty.call(object, property);
    };

    requireModule.j = 6899;

    // Webpack chunk loader
    (function() {
        var chunkStatus = { 6899: 0, 68: 0, 8973: 0, 5534: 0, 3625: 0 };

        // Define O.j with access to chunkStatus via closure
        requireModule.O.j = function(chunkId) {
            return chunkStatus[chunkId] === 0;
        };

        var chunkLoader = function(context, chunkData) {
            // chunkLoader is called as: chunkLoader.bind(null, 0) which means:
            // context = 0 (bound), chunkData = array element from forEach
            var chunks = chunkData;
            
            // Safety check - if chunks is not an array or doesn't have required structure, skip
            if (!chunks || !Array.isArray(chunks) || chunks.length < 1) {
                return;
            }
            
            var promises = chunks[0];
            var modules = chunks[1];
            var runtime = chunks[2];
            var deferred;

            // Check if promises is an array before calling .some()
            if (promises && Array.isArray(promises) && promises.length > 0) {
                var hasUnresolved = promises.some(function(chunkId) {
                    return chunkStatus[chunkId] !== 0;
                });

                if (hasUnresolved) {
                    if (modules) {
                        for (var moduleId in modules) {
                            if (requireModule.o(modules, moduleId)) {
                                requireModule.m[moduleId] = modules[moduleId];
                            }
                        }
                    }
                    if (runtime) deferred = runtime(requireModule);
                }

                // Process chunk status
                for (var i = 0; i < promises.length; i++) {
                    var chunkId = promises[i];
                    if (requireModule.o(chunkStatus, chunkId) && chunkStatus[chunkId]) {
                        if (typeof chunkStatus[chunkId][0] === 'function') {
                            chunkStatus[chunkId][0]();
                        }
                        chunkStatus[chunkId] = 0;
                    }
                }
            }

            return requireModule.O(deferred);
        };

        var webpackChunk = (self.webpackChunkworkflow = self.webpackChunkworkflow || []);
        
        // Process existing chunks - use bind to match original webpack pattern
        // chunkLoader.bind(null, 0) means: first arg = 0, second arg = array element
        if (webpackChunk && Array.isArray(webpackChunk)) {
            for (var i = 0; i < webpackChunk.length; i++) {
                chunkLoader(0, webpackChunk[i]);
            }
        }
        
        // Override push to process new chunks - match original webpack pattern
        var originalPush = webpackChunk.push.bind(webpackChunk);
        webpackChunk.push = function() {
            var result = originalPush.apply(this, arguments);
            for (var i = 0; i < arguments.length; i++) {
                chunkLoader(0, arguments[i]);
            }
            return result;
        };
    })();

    // Load main navigation module directly (bypass webpack chunk system for main module)
    try {
        var navModule = requireModule(2483);
        // Module should execute and register custom elements
        // Check if custom elements are registered
        if (window.customElements.get("main-nav")) {
            // Successfully registered
        } else {
            console.warn('main-nav custom element was not registered');
        }
    } catch (e) {
        console.error('Error loading navigation module:', e);
        console.error(e.stack);
    }
})();
